/**
 * The formula a `derived.toughness` modification carries (spec 2026-09-18 §3–§4):
 *
 *     (M * ${element}) + B
 *
 * M is a non-negative decimal multiplier, B an integer bonus, and `${element}` a literal placeholder
 * standing for the attribute named by `configuration.element`. Pure — no store import, like
 * container-carry.ts. The configurator composes the string, the Toughness recalc parses it, and
 * nothing else touches the grammar, so it has exactly one implementation.
 */

export const DERIVED_TOUGHNESS_PATH = 'derived.toughness';

export const DERIVED_SOURCE_ATTRIBUTES = ['agility', 'smarts', 'spirit', 'strength', 'vigor'] as const;
export type DerivedSourceAttribute = typeof DERIVED_SOURCE_ATTRIBUTES[number];

export type DerivedFormulaParts = { multiplier: number; bonus: number };

/** The stock Toughness base — what calculateToughness computed before this path existed. */
export const STOCK_TOUGHNESS_FORMULA: DerivedFormulaParts = { multiplier: 0.5, bonus: 2 };

// Whitespace-tolerant, sign-aware; the multiplier group admits no sign, so a negative multiplier
// cannot parse by construction (spec §9).
const FORMULA = /^\(\s*(\d+(?:\.\d+)?)\s*\*\s*\$\{element\}\s*\)\s*([+-])\s*(\d+)\s*$/;

export function isDerivedSourceAttribute(element: unknown): element is DerivedSourceAttribute {
    return typeof element === 'string' && (DERIVED_SOURCE_ATTRIBUTES as readonly string[]).includes(element);
}

export function composeDerivedFormula({ multiplier, bonus }: DerivedFormulaParts): string {
    const sign = bonus < 0 ? '-' : '+';
    return `(${multiplier} * \${element}) ${sign} ${Math.abs(bonus)}`;
}

/** The parts of a canonical string, or null for a number, null, or any other shape. */
export function parseDerivedFormula(value: unknown): DerivedFormulaParts | null {
    if (typeof value !== 'string') return null;
    const match = FORMULA.exec(value.trim());
    if (!match) return null;
    const multiplier = Number(match[1]);
    const bonus = Number(match[3]) * (match[2] === '-' ? -1 : 1);
    if (!Number.isFinite(multiplier) || !Number.isFinite(bonus)) return null;
    return { multiplier, bonus };
}

/** Floors the product before the bonus — the same rounding as the stock `2 + floor(vigor / 2)`. */
export function evaluateDerivedFormula({ multiplier, bonus }: DerivedFormulaParts, sourceValue: number): number {
    return Math.floor(multiplier * sourceValue) + bonus;
}

/** Display text for the generated label only; the stored value never carries the attribute's name. */
export function describeDerivedFormula({ multiplier, bonus }: DerivedFormulaParts, attributeLabel: string): string {
    const head = `${multiplier} × ${attributeLabel}`;
    if (bonus === 0) return head;
    return `${head} ${bonus < 0 ? '-' : '+'} ${Math.abs(bonus)}`;
}
